import { forEach } from 'lodash-es';
import { eventBus } from '@tools/event-bus';
import { GoogleGeocoding } from '@tools/geoservices/google-geocoding';
import { googlePlacesHelpers } from '@tools/geoservices/google-places-helpers';
import { Logger } from '@tools/logger/logger';
import { scaleUpToSmallestViewport } from '@tools/geoservices/viewport';

/**
 * Custom class google autocomplete.
 * the standard google.maps.places.Autocomplete api did not allow needed behaviour.
 *
 * @deprecated This class should be removed and replaced by using @googlemaps/js-api-loader
 */
export class AutocompleteCustom {
  /**
   * Constructor of autocomplete element
   * @param searchField       dom element where we setup autocomplete
   */
  constructor(searchField) {
    this._searchField = searchField;
    this._clearSpan = null;
    this.dataInputLocation = null;
    this.autocompleteList = null;
    this.placeIdList = [];
    this.place = {};
    this.currentSelectedIndex = 0;
    this.currentSelectedPlace = 0;
    this.searchPlaceholder = '';
    this.geoService = new GoogleGeocoding(searchField);
  }

  enablePlaces() {
    this.geoService.enablePlaces();
  }

  hideAutocompleteSuggestions() {
    this.autocompleteList.classList.remove('pac-container__displayed');
  }

  init() {
    this.dataInputLocation = this._searchField.parentNode.parentNode;
    this.searchPlaceholder = this._searchField.getAttribute('placeholder');
    if (this._searchField.value !== '') {
      [ this._searchField.value ] = this._searchField.value.split(',');
    }
    this.initResultListWrapper();
    this.addEventsListeners();
  }

  /* *****************************************************************************
   * ****************************** METHODS **************************************
   * **************************************************************************** */

  /**
   * initResultListWrapper
   * @description Init autocomplete results list container, and set dynamic position
   */
  initResultListWrapper() {
    const resultList = document.createElement('ul');
    resultList.classList.add('pac-container');
    this._searchField.parentNode.insertBefore(resultList, this._searchField.nextSibling);

    this.autocompleteList = resultList;
  }

  /**
   * buildAutocompletePredictionsList
   * @param {array} predictions
   * @description Build autocomplete predictions list, and select first prediction.
   */
  buildAutocompletePredictionsList(predictions) {
    this.clearAutocompleteResults();

    const firstPredictions = predictions.slice(0, 4);

    if (!firstPredictions.length) {
      return;
    }

    let previousLabel = '';
    forEach(firstPredictions, (prediction, index) => {
      this.placeIdList.push(prediction.id);
      const liElement = document.createElement('li');

      liElement.classList.add('pac-item');

      if (index === 0) {
        liElement.classList.add('pac-item-selected');
      }

      liElement.dataset.index = index;
      liElement.dataset.name = prediction.name;
      liElement.dataset.href = prediction.url || '';
      liElement.innerHTML = `<p><span class="prediction">${prediction.template || prediction.name}</span></p>`;

      if (prediction.label && prediction.label !== previousLabel) {
        liElement.dataset.label = prediction.label;
        previousLabel = prediction.label;
      }

      this.autocompleteList.appendChild(liElement);
    });

    this.autocompleteList.classList.add('pac-container__displayed');
  }

  /**
   * clearAutocompleteResults
   * @description Remove all predictions in autocomplete result list,
   * and reset variables related to it.
   */
  clearAutocompleteResults() {
    this.placeIdList = [];
    this.currentSelectedIndex = 0;
    this.currentSelectedPlace = 0;

    while (this.autocompleteList.firstChild) {
      this.autocompleteList.removeChild(this.autocompleteList.firstChild);
    }
  }

  /**
   * selectAutocompleteItem
   */
  async selectAutocompleteItem(disableChangePage = false) {
    /* @see https://bureaux-a-partager.atlassian.net/browse/MATCH-3073 */

    if (this.autocompleteList.children.length === 0) {
      return;
    }

    const autocompleteValue = this.autocompleteList.children[this.currentSelectedIndex].textContent;

    if (this.placeIdList[this.currentSelectedPlace]) {
      await this.retrievePlaceDetails(this.placeIdList[this.currentSelectedPlace]);
      if (!disableChangePage) {
        eventBus.dispatchEvent(new CustomEvent('input-location-change', {
          detail: {
            place: this.place,
            field: this._searchField,
          },
        }));
      }
    }

    if (autocompleteValue !== '') {
      let lastComma = autocompleteValue.lastIndexOf(',');
      if (lastComma === -1) {
        lastComma = autocompleteValue.length;
      }
      const postalCode = googlePlacesHelpers.extractComponentFromPlace(
        'postal_code',
        this.place,
      );
      const locality = googlePlacesHelpers.extractComponentFromPlace(
        'locality',
        this.place,
      );
      const firstAddressPart = autocompleteValue
        .split(',', 1)[0]
        .split('•', 1)[0]
        .trim()
        .slice(0, lastComma)
        .trim();
      this._searchField.value = `${firstAddressPart}`;
      if (postalCode !== null && locality !== firstAddressPart) {
        this._searchField.value = `${firstAddressPart} ${postalCode} ${locality}`;
      }
    }

    this.hideAutocompleteSuggestions();
  }

  /**
   * setInputElementData
   * @param {Object} place
   * @description Set hidden inputs with geoloc coordinates.
   */
  setInputElementData(place) {
    if (!place.name || !place.viewport) {
      return;
    }
    // if current place does not contain a street number or is a POI, upgrade viewport
    // to smallest acceptable size (see around the point of interest)
    // @see: https://bureaux-a-partager.atlassian.net/browse/MATCH-4361
    if (googlePlacesHelpers.extractComponentFromPlace('street_number', place) === null
      || place.types.includes('point_of_interest')) {
      place.viewport = scaleUpToSmallestViewport(place.viewport); // eslint-disable-line no-param-reassign
    }

    const northeastCoords = this.dataInputLocation.getElementsByClassName('northeast');
    const southwestCoords = this.dataInputLocation.getElementsByClassName('southwest');

    if (northeastCoords.length && southwestCoords.length) {
      northeastCoords[0].getElementsByClassName('latitude')[0].value = place.viewport.north;
      northeastCoords[0].getElementsByClassName('longitude')[0].value = place.viewport.east;
      southwestCoords[0].getElementsByClassName('latitude')[0].value = place.viewport.south;
      southwestCoords[0].getElementsByClassName('longitude')[0].value = place.viewport.west;
    }
    const isLocalityInput = this.dataInputLocation.getElementsByClassName('islocality');
    if (isLocalityInput.length) {
      isLocalityInput[0].value = 'false';

      // Current place is a locality, prefill hidden city and zipcode input
      // with appropriate address component data
      if (place.isLocality) {
        const zipCodeInput = this.dataInputLocation.getElementsByClassName('zipcode');
        const cityInput = this.dataInputLocation.getElementsByClassName('city');
        if (zipCodeInput.length) {
          zipCodeInput[0].value = googlePlacesHelpers.extractComponentFromPlace('postal_code', place);
          cityInput[0].value = googlePlacesHelpers.extractComponentFromPlace('locality', place);
          isLocalityInput[0].value = 'true';
        }
        this.dataInputLocation.querySelector('.center .latitude').value = place.location.latitude;
        this.dataInputLocation.querySelector('.center .longitude').value = place.location.longitude;
      }
    }
  }

  /* *****************************************************************************
   * ***************************** API CALLS *************************************
   * **************************************************************************** */

  /**
   * triggerPredictionsSearch
   * @description Get predictions based on input value,
   * then trigger buildAutocompletePredictionsList
   */
  triggerPredictionsSearch() {
    // If input value empty, or with spaces, clear result list and do not search for predictions.
    if (this._searchField.value.trim() === '') {
      this.clearAutocompleteResults();
      this.buildAutocompletePredictionsList([]);
      return;
    }

    eventBus.dispatchEvent(new CustomEvent('custom-autocomplete-triggered', { detail: this._searchField }));

    this.geoService.autocomplete()
      .then((predictions) => this.buildAutocompletePredictionsList(predictions))
      .catch(Logger.error);
  }

  /**
   * retrievePlaceDetails
   * @param placeId
   * @description Get all details of a place, using its place_id.
   */
  retrievePlaceDetails(placeId) {
    return this.geoService.details(placeId)
      .then((place) => {
        this.place = place;

        this.place = googlePlacesHelpers.computeLocalityFlag(this.place);
        this.place = googlePlacesHelpers.computePostalCode(this.place);
        this.place = googlePlacesHelpers.computeViewport(this.place);

        this.setInputElementData(this.place);
      })
      .catch(Logger.error);
  }

  /* *****************************************************************************
   * *************************** EVENT LISTENERS *********************************
   * **************************************************************************** */

  /**
   * addEventsListeners
   * @description Group all events binded onto input search, or dropdown results list.
   */
  addEventsListeners() {
    if (document.getElementById('hero_filters_ok')) {
      document.getElementById('hero_filters_ok').addEventListener('click', () => this.selectAutocompleteItem());
    }
    this._searchField.addEventListener('keydown', (event) => this.autocompleteKeydownListener(event));
    this._searchField.addEventListener('keyup', (event) => this.autocompleteKeyupListener(event));
    this._searchField.addEventListener('click', () => this.triggerPredictionsSearch());
    this.autocompleteList.addEventListener('click', (event) => this.autocompleteClickListener(event));
    window.addEventListener('click', (event) => this.clickOutsideSearchListener(event));
  }

  /**
   * autocompleteKeydownListener
   * @param event
   */
  autocompleteKeydownListener(event) {
    // TABS key
    if (event.keyCode === 9) {
      this.hideAutocompleteSuggestions();
    }
  }

  /**
   * autocompleteKeyupListener
   * @param event
   * @description Execute methods related to specific keys up event.
   */
  autocompleteKeyupListener(event) {
    const selectAutocompleteItem = () => {
      const element = document.getElementsByClassName('pac-item-selected')[0];
      this.currentSelectedIndex = [].indexOf.call(element.parentNode.children, element);
      this.currentSelectedPlace = element.dataset.index;
      this.selectAutocompleteItem();
    };

    switch (event.keyCode) {
      case 38:
        // UP
        this.upKeyAutocompleteInteraction();
        break;

      case 40:
        // DOWN
        this.downKeyAutocompleteInteraction();
        break;

      case 13:
        // ENTER
        // Fetch selected element and update the index
        selectAutocompleteItem();
        break;

      default:
        this.triggerPredictionsSearch();
    }
  }

  /**
   * autocompleteClickListener
   * @param event
   * @description Select prediction on click
   */
  autocompleteClickListener(event) {
    let element = event.target;
    if (element.tagName !== 'li') {
      const closestLi = element.closest('li');
      element = closestLi;
    }
    this.currentSelectedIndex = [].indexOf.call(element.parentNode.children, element);
    this.currentSelectedPlace = element.dataset.index;
    this.selectAutocompleteItem();
  }

  /**
   * upKeyAutocompleteInteraction
   * @description Select uper item in predictions list
   */
  upKeyAutocompleteInteraction() {
    if (this.currentSelectedIndex !== 0) {
      this.autocompleteList.children[this.currentSelectedIndex].classList.remove('pac-item-selected');
      this.currentSelectedIndex -= 1;
      this.autocompleteList.children[this.currentSelectedIndex].classList.add('pac-item-selected');
    }
  }

  /**
   * downKeyAutocompleteInteraction
   * @description Select lower item in predictions list
   */
  downKeyAutocompleteInteraction() {
    const countListResults = this.autocompleteList.children.length;

    if (this.currentSelectedIndex !== (countListResults - 1)) {
      this.autocompleteList.children[this.currentSelectedIndex].classList.remove('pac-item-selected');
      this.currentSelectedIndex += 1;
      this.autocompleteList.children[this.currentSelectedIndex].classList.add('pac-item-selected');
    }
  }

  /**
   * clickOutsideSearchListener
   * @param event
   * @description hide predictions list on click outside input search & predictions list
   */
  clickOutsideSearchListener(event) {
    if (
      this.autocompleteList.classList.contains('pac-container__displayed')
      && !this._searchField.parentNode.contains(event.target)
    ) {
      this.selectAutocompleteItem(true);
      this.hideAutocompleteSuggestions();
    }
  }
}
